import React, { TextareaHTMLAttributes, forwardRef, useRef } from 'react';
import { useAutoResizeTextarea } from '../../hooks/useAutoResizeTextarea';
import AiCornerActions, { AiCornerActionsProps } from './AiCornerActions';

interface AiEnhancedTextareaProps
  extends TextareaHTMLAttributes<HTMLTextAreaElement>,
    Omit<AiCornerActionsProps, 'anchor'> {
  label?: string;
  error?: string;
  helperText?: string;
  minRows?: number;
  maxRows?: number;
}

const AiEnhancedTextarea = forwardRef<HTMLTextAreaElement, AiEnhancedTextareaProps>(
  (
    {
      label,
      error,
      helperText,
      className = '',
      minRows = 4,
      maxRows = 20,
      onChange,
      value,
      onQuickAction,
      onMenuAction,
      isLoading = false,
      hasContent,
      disabled,
      showRephraseOptions = true,
      showChatOption = false,
      showMenu = true,
      quickActionIcon,
      bookId,
      quickActionLabel,
      size = 'md',
      ...props
    },
    ref
  ) => {
    const internalRef = useRef<HTMLTextAreaElement>(null);
    const textareaRef = (ref as React.RefObject<HTMLTextAreaElement>) || internalRef;
    const stringValue = String(value ?? '');

    const { adjustHeight } = useAutoResizeTextarea(textareaRef, stringValue, {
      minRows,
      maxRows,
    });

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      onChange?.(e);
      adjustHeight();
    };

    const computedHasContent =
      typeof hasContent === 'boolean' ? hasContent : !!stringValue.trim();
    const isSm = size === 'sm';
    const fieldClass = isSm
      ? `w-full pl-3 pr-16 py-2 rounded-lg bg-white dark:bg-gray-800 border ${error ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} hover:border-primary-400 dark:hover:border-primary-600 focus:outline-none focus:ring-2 ${error ? 'focus:ring-red-500/20' : 'focus:ring-primary-500/20'} focus:border-primary-500 dark:focus:border-primary-400 text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 transition-colors resize-none`
      : `w-full pl-4 pr-20 py-3 rounded-xl bg-white dark:bg-gray-800 border-2 ${error ? 'border-red-500' : 'border-gray-200 dark:border-gray-700'} hover:border-primary-400 dark:hover:border-primary-600 focus:outline-none focus:ring-4 ${error ? 'focus:ring-red-500/20' : 'focus:ring-primary-500/20'} focus:border-primary-500 dark:focus:border-primary-400 text-gray-900 dark:text-gray-100 font-medium placeholder:text-gray-400 dark:placeholder:text-gray-500 transition-all duration-300 shadow-sm hover:shadow-md resize-none`;

    return (
      <div className="w-full">
        {label && (
          <label className={`block ${isSm ? 'text-xs' : 'text-sm'} font-semibold text-gray-700 dark:text-gray-300 mb-2`}>
            {label}
          </label>
        )}
        <div className="relative">
          <textarea
            ref={textareaRef}
            value={value}
            onChange={handleChange}
            disabled={disabled}
            className={`${fieldClass} ${className}`}
            {...props}
          />
          <AiCornerActions
            anchor="textarea"
            size={size}
            onQuickAction={onQuickAction}
            onMenuAction={onMenuAction}
            isLoading={isLoading}
            hasContent={computedHasContent}
            disabled={disabled}
            showRephraseOptions={showRephraseOptions}
            showChatOption={showChatOption}
            showMenu={showMenu}
            quickActionIcon={quickActionIcon}
            bookId={bookId}
            quickActionLabel={quickActionLabel}
          />
        </div>
        {error && (
          <p className="mt-2 text-sm text-red-500 font-medium flex items-center gap-1">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
            {error}
          </p>
        )}
        {helperText && !error && (
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">{helperText}</p>
        )}
      </div>
    );
  }
);

AiEnhancedTextarea.displayName = 'AiEnhancedTextarea';

export default AiEnhancedTextarea;
